// Original blog content authored for the AI CRM landing page.
// Topics are inspired by typical posts on AI-native CRM / Customer Interaction Platforms,
// but every line is written from scratch for this project.

export const blogs = [
  {
    slug: "beyond-basic-chatbots-conditional-prompting",
    title:
      "Beyond basic chatbots: how conditional prompting rewrote customer conversations",
    teaser:
      "Old chatbots greet every midnight-panicking customer with the same cheerful hello. Conditional prompting lets your agent read the room — and answer like a human would.",
    category: "Agents",
    readTime: "6 min",
    date: "Dec 04, 2025",
    author: "Arpit Agrawal",
    role: "CEO, AI CRM",
    hero: "https://images.unsplash.com/photo-1644268756918-16348d1bc619?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2OTV8MHwxfHNlYXJjaHwyfHxwcm9mZXNzaW9uYWwlMjBjb3Jwb3JhdGUlMjBhdmF0YXIlMjBoZWFkc2hvdHxlbnwwfHx8fDE3NzY5ODUyNzB8MA&ixlib=rb-4.1.0&q=85",
    visual: "flow",
    sections: [
      {
        h: "The problem with scripted bots",
        p: "A decade of rule-based chatbots trained an entire generation of customers to type 'human' the moment a conversation opens. The bots didn't fail because they were dumb — they failed because they treated every customer, every hour of the day, and every intent as if they were the same.",
      },
      {
        h: "What conditional prompting actually means",
        p: "Conditional prompting is the simple idea that the words your agent generates should change based on who it's talking to, what channel they came from, and what the running state of the conversation is. Time of day, sentiment, order history, previous tickets — all of it becomes part of the prompt at run-time.",
      },
      {
        h: "An example worth copying",
        p: "A tired customer on WhatsApp at 2 am with a refund issue doesn't need 'How can I help you today?' — they need 'I can see your order A-2381 was charged twice. I'm refunding now, and I'll text you the receipt in 90 seconds.' Same model, same knowledge base, radically different first impression. That's what a conditional prompt unlocks.",
      },
      {
        h: "How we do it at Angoor",
        p: "Every agent in our platform has a base system prompt and a set of conditional blocks. Blocks activate based on channel, intent, customer segment, or any tool call that has already happened upstream in the flow. The result: your agent sounds different to a VIP on email than to a brand-new web visitor — by design, not by accident.",
      },
    ],
  },
  {
    slug: "cdps-to-crm-rescue",
    title: "Toodle-oo CDPs. AI-native CRMs are here to rescue B2C companies.",
    teaser:
      "A decade of CDPs taught B2C teams how to store customer data. None of it taught their reps how to act on it in real time. The CRM is back — and this time it thinks.",
    category: "Strategy",
    readTime: "7 min",
    date: "Nov 28, 2025",
    author: "Rishabh Kumar",
    role: "CPO, AI CRM",
    hero: "https://images.unsplash.com/photo-1609371497456-3a55a205d5eb?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2OTV8MHwxfHNlYXJjaHwxfHxwcm9mZXNzaW9uYWwlMjBjb3Jwb3JhdGUlMjBhdmF0YXIlMjBoZWFkc2hvdHxlbnwwfHx8fDE3NzY5ODUyNzB8MA&ixlib=rb-4.1.0&q=85",
    visual: "stack",
    sections: [
      {
        h: "Why the CDP era ended quietly",
        p: "CDPs promised a single view of the customer. They delivered a very expensive data warehouse with a marketing-friendly UI on top. The data was clean, the dashboards were beautiful, and the revenue impact was… hard to find on a deck.",
      },
      {
        h: "The missing layer was action",
        p: "Storing customer preferences is table stakes. Acting on them the moment a customer reaches out is where money is made. That requires the database to live next to the conversation, and the conversation to live next to the tools that can actually do something.",
      },
      {
        h: "What an AI-native CRM looks like",
        p: "One platform that holds the customer record, the conversation thread, the tool connectors, and the agent that orchestrates the three. No sync jobs, no middleware, no ETL lag. A refund conversation triggered on WhatsApp updates the order record, writes back to the CRM field, and fires the Slack escalation — all in a single run.",
      },
      {
        h: "The bet we're making",
        p: "B2C brands are going to stop buying a CDP, a helpdesk, a marketing tool and a flow builder separately. They'll buy one platform that handles all four, with agents acting as the connective tissue between them. That's what we're building.",
      },
    ],
  },
  {
    slug: "microsoft-vs-salesforce",
    title: "Microsoft vs Salesforce — who's right about the future of CRM?",
    teaser:
      "Satya Nadella says CRM is dead. Marc Benioff says agents are just marketing. They can't both be right. They might both be wrong.",
    category: "Industry",
    readTime: "8 min",
    date: "Nov 18, 2025",
    author: "Rishabh Kumar",
    role: "CPO, AI CRM",
    hero: "https://images.unsplash.com/photo-1633625510483-c177f4308f33?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2OTV8MHwxfHNlYXJjaHw0fHxwcm9mZXNzaW9uYWwlMjBjb3Jwb3JhdGUlMjBhdmF0YXIlMjBoZWFkc2hvdHxlbnwwfHx8fDE3NzY5ODUyNzB8MA&ixlib=rb-4.1.0&q=85",
    visual: "split",
    sections: [
      {
        h: "The public argument",
        p: "Microsoft's pitch is that Copilot will eat every SaaS vertical — starting with CRM. Salesforce's counter is that agents without a system of record are a toy, and that Agentforce stitched into Data Cloud is the only defensible play.",
      },
      {
        h: "Both are partly right",
        p: "Microsoft is right that CRMs, as a category, were built for 2005 — forms, fields and pipelines optimised for humans manually moving records. Salesforce is right that agents without context are unreliable, and context lives in the system of record.",
      },
      {
        h: "What actually changes",
        p: "The shape of the CRM changes, not the existence of it. The front-end collapses from 400 form fields to a conversation. The back-end expands from static tables to a tool layer that any agent can call. The moat stops being 'we have the fields' and becomes 'we have the reliable actions'.",
      },
      {
        h: "Where the next winner sits",
        p: "The AI-native CRM doesn't look like either of them today. It looks like a thin conversation layer on top of a wide tool layer, with knowledge bases where the static schema used to live. Whichever platform gets that stack right — at startup price, not Fortune 500 price — earns the next decade.",
      },
    ],
  },
  {
    slug: "modern-saas-is-in-a-hearse",
    title: "Modern SaaS is in a hearse. Here's what's replacing it.",
    teaser:
      "Seat-based pricing, forms-based UX, weekly syncs to the data warehouse. None of this survives the next five years — and none of it deserves to.",
    category: "Opinion",
    readTime: "9 min",
    date: "Nov 10, 2025",
    author: "Rishabh Kumar",
    role: "CPO, AI CRM",
    hero: "https://images.unsplash.com/photo-1768247695726-022586dea3a1?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2OTV8MHwxfHNlYXJjaHwzfHxwcm9mZXNzaW9uYWwlMjBjb3Jwb3JhdGUlMjBhdmF0YXIlMjBoZWFkc2hvdHxlbnwwfHx8fDE3NzY5ODUyNzB8MA&ixlib=rb-4.1.0&q=85",
    visual: "chat",
    sections: [
      {
        h: "The three lies of classic SaaS",
        p: "Lie one: seats are a fair unit of value. Lie two: a human should manually move a record from column to column. Lie three: the right place to store customer truth is a proprietary schema you pay someone else to migrate every five years.",
      },
      {
        h: "What the new shape looks like",
        p: "Value gets billed per outcome, not per login. Records get moved by agents with tool access, not humans with mice. Customer truth lives in an embedding index you own — not in a table a vendor owns. The platform earns its margin on the work done, not on the license signed.",
      },
      {
        h: "Why customer-facing software goes first",
        p: "The first category to flip is customer interaction. Support, sales, success — they all live on conversations, and conversations are exactly what LLMs are good at. The second wave will be internal: HR, ops, procurement. The pattern is the same; the verticals differ.",
      },
      {
        h: "A cheerful funeral",
        p: "SaaS isn't dying so much as graduating. The good parts — multi-tenant infrastructure, self-serve onboarding, product-led growth — stay. The bad parts — seat inflation, schema lock-in, 'implementation partners' — get buried. We're just speeding up the procession.",
      },
    ],
  },
  {
    slug: "whatsapp-business-api-2026",
    title: "The WhatsApp Business API in 2026: what actually matters",
    teaser:
      "Everyone is launching a WhatsApp bot. Very few are shipping WhatsApp experiences. Here's what separates them — and what your CX team should ignore.",
    category: "Channels",
    readTime: "5 min",
    date: "Oct 30, 2025",
    author: "Arpit Agrawal",
    role: "CEO, AI CRM",
    hero: "https://images.unsplash.com/photo-1644268756918-16348d1bc619?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2OTV8MHwxfHNlYXJjaHwyfHxwcm9mZXNzaW9uYWwlMjBjb3Jwb3JhdGUlMjBhdmF0YXIlMjBoZWFkc2hvdHxlbnwwfHx8fDE3NzY5ODUyNzB8MA&ixlib=rb-4.1.0&q=85",
    visual: "chat",
    sections: [
      {
        h: "The template tax",
        p: "Template approvals are still the single biggest bottleneck teams hit when they go live on WhatsApp. A good platform pre-drafts the template, submits it, tracks approval, and falls back to an interactive session if the template is rejected — without any engineering ticket being filed.",
      },
      {
        h: "Interactive > text",
        p: "Buttons, lists, flows and native WhatsApp forms do twice the job of a wall of text. The teams winning on WhatsApp aren't writing better copy — they're reducing the copy and replacing it with one-tap actions the agent already knows how to interpret.",
      },
      {
        h: "Opt-in is a product problem, not a legal one",
        p: "Most teams treat opt-in as a compliance checkbox. It is — but it's also where you lose 40% of your audience. Opt-in collected in context, right after value has been delivered, converts 3x better than a generic 'subscribe to updates' form on your site.",
      },
      {
        h: "What we ship at Angoor",
        p: "Our WhatsApp channel handles template lifecycle, session windows, media re-uploads, and interactive flows out of the box. Your agent just replies — everything else is the platform's problem.",
      },
    ],
  },
  {
    slug: "voice-agents-that-dont-sound-like-robots",
    title: "Voice agents that don't sound like robots",
    teaser:
      "Two years in, most AI voice agents still fail the first 10 seconds. The fix isn't a better model — it's better latency budgeting and better silence.",
    category: "Agents",
    readTime: "6 min",
    date: "Oct 14, 2025",
    author: "Arpit Agrawal",
    role: "CEO, AI CRM",
    hero: "https://images.unsplash.com/photo-1609371497456-3a55a205d5eb?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2OTV8MHwxfHNlYXJjaHwxfHxwcm9mZXNzaW9uYWwlMjBjb3Jwb3JhdGUlMjBhdmF0YXIlMjBoZWFkc2hvdHxlbnwwfHx8fDE3NzY5ODUyNzB8MA&ixlib=rb-4.1.0&q=85",
    visual: "flow",
    sections: [
      {
        h: "The first 10 seconds",
        p: "A customer picks up, says hello, and the agent takes 1.4 seconds to respond. The conversation is already ruined. Human agents answer within 500 ms — not because they think faster, but because they fill the space with acknowledgement tokens while they plan what to say.",
      },
      {
        h: "Budget latency like a designer",
        p: "Total perceived latency is a sum: microphone, speech-to-text, LLM round-trip, text-to-speech, network. Shave 200 ms off each, and you're below the human threshold. The single biggest win is streaming the TTS output before the LLM has finished generating.",
      },
      {
        h: "Silence is a feature",
        p: "Good voice agents pause after the customer's question, the way a human would. Bad voice agents jump in the instant the VAD detects silence, interrupting half the customer's sentences. A 400 ms grace window doubles the perceived intelligence for free.",
      },
      {
        h: "Where we are today",
        p: "Angoor's voice channel runs a streaming STT → interruptible LLM → streaming TTS pipeline with a configurable grace window per agent. First-response time on our median call is under 600 ms, and customers stop asking 'is this a robot?' within the first minute.",
      },
    ],
  },
];

export function getBlog(slug) {
  return blogs.find((b) => b.slug === slug);
}
